// The super admin's console (PLAT-01..11, D-182): its frame and navigation, and its Revenue, Growth and Tech pages.
// The Dashboard and Customers pages are in PlatformHome.tsx, each company on its own page (PlatformOrg.tsx). Never
// tenant rows, calls or recordings: companies are seen by their counts, and a team only when asked for (D-183).
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router";
import { Brand, DemoBanner, Mark } from "../app/Layout";
import { useSession } from "../app/session";
import { Card, ErrorNote, Field, Loading, Modal, Page, useAction, useLoad } from "../app/ui";
import { BUSINESS_TYPES, type BusinessType } from "../lib/aiInstructions";
import { fmtDateTime, fmtNumber } from "../lib/format";
import { phoneAsksFor } from "../lib/readiness";
import type { AddOns, PlatformOrg } from "../lib/types";
import { AiUsageCard, HealthCard, IntegrationDemandCard, InvoicesCard } from "./PlatformOps";

export const TZ = "Asia/Kolkata";

/** "6 calls waiting": what a company saved while under review. */
export function waiting(n: number): string {
  return `${fmtNumber(n)} ${n === 1 ? "call" : "calls"} waiting`;
}

export function industryName(vertical: BusinessType): string {
  return BUSINESS_TYPES.find((b) => b.value === vertical)?.label ?? vertical;
}

/** The two add-ons on the CRM (D-081), as the brief describes them. */
export const ADD_ONS: { key: keyof AddOns; name: string; says: string }[] = [
  {
    key: "coach",
    name: "Coach",
    says: "AI reviews its calls: a checklist for each call, tips for its callers, trends for the owner.",
  },
  {
    key: "autopilot",
    name: "Autopilot",
    says: "AI on every call: summary, customer details, lead score, follow-ups and the next call's brief.",
  },
];

/** "a, b and c". */
export function andList(items: string[]): string {
  return items.length < 2 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** The add-on checkboxes, with what the company's phones will ask for (APP-26). */
export function AddOnChoices(
  { value, onChange, keepsRecordings = false }: {
    value: AddOns;
    onChange: (next: AddOns) => void;
    /** It keeps its recordings without AI, from its own settings (D-170). */
    keepsRecordings?: boolean;
  },
) {
  return (
    <div className="stack tight" style={{ marginBottom: 14 }}>
      <span className="faint">
        The CRM is always on: customers, Today, reminders and follow-ups, with no AI.
      </span>
      {ADD_ONS.map((a) => (
        <label className="row" key={a.key} style={{ alignItems: "flex-start", flexWrap: "nowrap" }}>
          <input
            type="checkbox"
            checked={value[a.key]}
            onChange={(e) => onChange({ ...value, [a.key]: e.target.checked })}
            style={{ marginTop: 3 }}
          />
          <span>
            <strong>{a.name}</strong>
            <br />
            <span className="faint">{a.says}</span>
          </span>
        </label>
      ))}
      <span>
        Its phones ask for {andList(phoneAsksFor(value, keepsRecordings))}.
        {!value.coach && !value.autopilot && (keepsRecordings
          ? " It keeps its recordings, from its own settings, and no AI hears them."
          : " No call goes to the AI, and no recording leaves the phones unless the company keeps them in its settings.")}
      </span>
    </div>
  );
}

/** "Coach · Autopilot", or "CRM only"; and "Team of one" for a company of one person (D-169). */
export function AddOnBadges({ addOns, memberLimit }: { addOns: AddOns; memberLimit?: number | null }) {
  const on = ADD_ONS.filter((a) => addOns[a.key]);
  return (
    <span className="row" style={{ gap: 4 }}>
      {memberLimit === 1 && <span className="badge warm">Team of one</span>}
      {on.length === 0 && <span className="badge">CRM only</span>}
      {on.map((a) => <span className="badge brand" key={a.key}>{a.name}</span>)}
    </span>
  );
}

/** "1 person", "5 people", or "No limit". */
export function peopleAllowed(limit: number | null): string {
  return limit === null ? "No limit" : limit === 1 ? "1 person" : `${fmtNumber(limit)} people`;
}

/** The people-limit input's text as a limit: empty is no limit (D-169). */
export function parseLimit(text: string): number | null {
  return text.trim() === "" ? null : Number(text);
}

/** The console's sections, one per audience: business, support, sales and tech (D-182). */
const SECTIONS: { to: string; label: string; also?: RegExp }[] = [
  { to: "/", label: "Dashboard" },
  { to: "/customers", label: "Customers", also: /^\/(customers|org)\b/ },
  { to: "/revenue", label: "Revenue" },
  { to: "/growth", label: "Growth" },
  { to: "/tech", label: "Tech" },
];

/** The console's frame: its sections in a sidebar (a menu on a phone), sign out, and the page. */
export function PlatformShell({ children }: { children: ReactNode }) {
  const { signOut } = useSession();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  const current = SECTIONS.find((s) => s.also?.test(location.pathname) || s.to === location.pathname);
  return (
    <>
      <DemoBanner />
      <div className="shell">
        {open && <div className="scrim" onClick={() => setOpen(false)} />}
        <aside className={`sidebar${open ? " open" : ""}`}>
          <Brand />
          <div className="org-name">Platform</div>
          <nav className="nav">
            {SECTIONS.map((s) => (
              <NavLink key={s.to} to={s.to} className={s === current ? "active" : ""}>
                {s.label}
              </NavLink>
            ))}
          </nav>
          <div className="sidebar-foot">
            <button className="link" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </aside>
        <div>
          <div className="topbar">
            <button className="small" onClick={() => setOpen(true)} aria-label="Open menu">
              ☰
            </button>
            <Mark size={20} />
            <strong>Platform</strong>
          </div>
          <main className="main">{children}</main>
        </div>
      </div>
    </>
  );
}

/** Revenue: each company's plan and AI minutes, the invoices, and what the AI costs. */
export function PlatformRevenue() {
  const { api } = useSession();
  const orgs = useLoad(() => api.platformOrgs(), [api]);
  return (
    <PlatformShell>
      <Page title="Revenue" sub="Plans, AI minutes and invoices.">
        <div className="stack">
          <ErrorNote error={orgs.error} />
          <Card flush title="Plans and minutes">
            {orgs.loading && !orgs.data && (
              <div style={{ padding: 16 }}>
                <Loading />
              </div>
            )}
            {orgs.data && <OrgTable orgs={orgs.data} />}
          </Card>
          <InvoicesCard />
          <AiUsageCard />
        </div>
      </Page>
    </PlatformShell>
  );
}

/** Growth: what companies ask Voxowa to connect to, and setting up a new company. */
export function PlatformGrowth() {
  const [creating, setCreating] = useState(false);
  return (
    <PlatformShell>
      <Page
        title="Growth"
        sub="What companies ask for, and new companies."
        actions={<button className="primary" onClick={() => setCreating(true)}>New organization</button>}
      >
        <IntegrationDemandCard />
      </Page>
      {creating && <NewOrganization onClose={() => setCreating(false)} onDone={async () => {}} />}
    </PlatformShell>
  );
}

/** Tech: the processing pipeline and its alerts, the AI switch, and everything the platform has done. */
export function PlatformTech() {
  const { api } = useSession();
  const audit = useLoad(() => api.platformAudit(), [api]);
  return (
    <PlatformShell>
      <Page title="Tech" sub="Processing, alerts and the platform's own actions.">
        <div className="stack">
          <HealthCard />
          <Card title="Platform actions">
            <ErrorNote error={audit.error} />
            <div className="list">
              {(audit.data ?? []).map((a, i) => (
                <div className="list-item row between" key={i}>
                  <span>
                    <strong>{a.org_name}</strong> · {a.action.replace(/\./g, " ")}{" "}
                    <span className="faint">{a.actor_email}</span>
                  </span>
                  <span className="faint">{fmtDateTime(a.at, TZ)}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </Page>
    </PlatformShell>
  );
}

function OrgTable({ orgs }: { orgs: PlatformOrg[] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Organization</th>
            <th>Plan</th>
            <th>Owner</th>
            <th className="num">People</th>
            <th className="num">Phones</th>
            <th>AI minutes</th>
            <th>Last activity</th>
            <th className="pin-end" />
          </tr>
        </thead>
        <tbody>
          {orgs.map((o) => {
            const owners = (o.owners as { name: string; email: string | null; phone: string | null }[]) ?? [];
            return (
              <tr key={o.id}>
                <td>
                  <div className="primary">
                    <Link to={`/org/${o.id}`}>{o.name}</Link>
                  </div>
                  {o.status === "suspended"
                    ? <span className="badge danger">Suspended: {o.suspended_reason}</span>
                    : o.reviewed_at === null
                    ? <span className="badge warm">Under review · {waiting(o.held_recordings)}</span>
                    : <span className="faint">Since {fmtDateTime(o.created_at, TZ)}</span>}
                </td>
                <td>
                  <AddOnBadges addOns={o.features} memberLimit={o.member_limit} />
                  <div className="faint">{industryName(o.vertical)}</div>
                </td>
                <td>
                  {owners.map((w) => (
                    <div key={w.name}>
                      {w.name} <span className="faint">{w.email ?? w.phone}</span>
                    </div>
                  ))}
                  {owners.length === 0 && <span className="faint">{o.pending_invitations ? "Invited" : "None"}</span>}
                </td>
                <td className="num">
                  {o.active_members} <span className="faint">({o.callers} callers)</span>
                </td>
                <td className="num">{o.devices}</td>
                <td className="nowrap">
                  {fmtNumber(o.ai_minutes_used)} / {fmtNumber(o.ai_minutes_included)}
                </td>
                <td className="nowrap">
                  {o.last_activity_at ? fmtDateTime(o.last_activity_at, TZ) : "–"}
                </td>
                <td className="pin-end">
                  <div className="row" style={{ justifyContent: "flex-end" }}>
                    <Link
                      className={`button small${o.reviewed_at === null ? " primary" : ""}`}
                      to={`/org/${o.id}`}
                    >
                      {o.reviewed_at === null ? "Review" : "Manage"}
                    </Link>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** A new organization: its name, industry and add-ons, and the owner it invites (PLAT-10). */
export function NewOrganization({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<void> }) {
  const { api } = useSession();
  const { busy, run } = useAction();
  const [name, setName] = useState("");
  const [vertical, setVertical] = useState<BusinessType>("real_estate");
  const [addOns, setAddOns] = useState<AddOns>({ coach: true, autopilot: true });
  const [limit, setLimit] = useState("");
  const [owner, setOwner] = useState("");
  const [contact, setContact] = useState("");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const who = contact.includes("@") ? { ownerEmail: contact.trim() } : { ownerPhone: contact.trim() };
    const ok = await run(
      () =>
        api.platformCreateOrg({
          name: name.trim(),
          ownerName: owner.trim(),
          vertical,
          addOns,
          memberLimit: parseLimit(limit),
          ...who,
        }),
      "Organization created; owner invited",
    );
    if (ok) {
      onClose();
      await onDone();
    }
  };

  return (
    <Modal title="New organization" onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Organization name">
          <input required value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label="Industry" hint="Its stages, lead sources, objection categories and call checklist.">
          <select value={vertical} onChange={(e) => setVertical(e.target.value as BusinessType)}>
            {BUSINESS_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </Field>
        <AddOnChoices value={addOns} onChange={setAddOns} />
        <PeopleAllowedField value={limit} onChange={setLimit} />
        <Field label="Owner's name">
          <input required value={owner} onChange={(e) => setOwner(e.target.value)} />
        </Field>
        <Field label="Owner's email or mobile" hint="They become the owner when they first sign in with it.">
          <input required value={contact} onChange={(e) => setContact(e.target.value)} />
        </Field>
        <div className="actions">
          <button type="button" onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy}>Create</button>
        </div>
      </form>
    </Modal>
  );
}

/** How many people a company may have (D-169): empty for no limit; 1 makes it a team of one. */
export function PeopleAllowedField({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  return (
    <Field
      label="People allowed"
      hint={value.trim() === "1"
        ? "A team of one: its one person is owner and telecaller, new customers are hers, and her dashboard shows " +
          "her own progress. Invite her by mobile, so she can sign in on the phone app too."
        : "Invitations count. Leave empty for no limit; 1 makes it a team of one."}
    >
      <input
        type="number"
        min={1}
        max={10000}
        placeholder="No limit"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}
